import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Lock,
  ArrowRight,
  Plus,
  Building2,
  ExternalLink,
  CheckCircle2,
  Eye,
  MapPinned,
  CircleOff,
  Layers,
  ListChecks,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorSubscriptionFeatures } from '../../services/subscriptionService';
import { useToastContext } from '../../contexts/ToastContext';
import {
  useVendorLocations,
  useCreateVendorLocation,
  useUpdateVendorLocation,
  useDeleteVendorLocation,
  useToggleVendorLocationStatus,
} from '../../services/vendorLocationService';
import LocationCard from '../../components/vendor/locations/LocationCard';
import LocationFormModal from '../../components/vendor/locations/LocationFormModal';
import VendorServiceAreas from '../../components/client/VendorServiceAreas';
import Button from '../../components/shared/Button';
import StatsGrid from '../../components/shared/StatsGrid';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorMultiLocation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToastContext();
  const { hasActiveSubscription, isChecking, hasFeature, isLoading: featureLoading } =
    useVendorSubscriptionFeatures('feature_multi_location');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const { data: locationsResponse, isLoading: locationsLoading } = useVendorLocations({
    enabled: hasActiveSubscription === true && hasFeature,
  });

  const createMutation = useCreateVendorLocation({
    onSuccess: () => {
      showSuccess('Location added', 'Your new service area is saved.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not add location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const updateMutation = useUpdateVendorLocation({
    onSuccess: () => {
      showSuccess('Location updated', 'Changes saved successfully.');
      closeModal();
    },
    onError: (err) => {
      showError('Could not update location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const deleteMutation = useDeleteVendorLocation({
    onSuccess: () => showSuccess('Location removed', 'Service area deleted.'),
    onError: (err) => {
      showError('Could not remove location', err?.response?.data?.message || 'Please try again.');
    },
  });

  const toggleMutation = useToggleVendorLocationStatus({
    onSuccess: (res) => {
      showSuccess('Status updated', res?.message || 'Location status changed.');
    },
    onError: (err) => {
      showError('Could not update status', err?.response?.data?.message || 'Please try again.');
    },
  });

  useEffect(() => {
    if (user?.user_type !== 'vendor') {
      navigate('/');
      return;
    }
    if (hasActiveSubscription === false) {
      navigate('/vendor/subscription', { replace: true });
    }
  }, [user?.user_type, hasActiveSubscription, navigate]);

  const pageData = locationsResponse?.data || {};
  const locations = pageData.locations || [];
  const primary = pageData.primary_location || {};
  const stats = pageData.statistics || {};
  const maxLocations = pageData.max_locations || 25;
  const canAddMore = (stats.remaining ?? maxLocations) > 0;
  const activeLocations = locations.filter((location) => location.is_active);

  const closeModal = () => {
    setModalOpen(false);
    setEditingLocation(null);
  };

  const openCreate = () => {
    setEditingLocation(null);
    setModalOpen(true);
  };

  const openEdit = (location) => {
    setEditingLocation(location);
    setModalOpen(true);
  };

  const handleSubmit = (payload) => {
    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleDelete = (location) => {
    if (!window.confirm(`Remove "${location.area_name}" from your service areas?`)) return;
    setDeletingId(location.id);
    deleteMutation.mutate(location.id, { onSettled: () => setDeletingId(null) });
  };

  const handleToggle = (location) => {
    setTogglingId(location.id);
    toggleMutation.mutate(location.id, { onSettled: () => setTogglingId(null) });
  };

  const primaryLabel = [primary.city, primary.state, primary.country].filter(Boolean).join(', ');
  const publicProfileUrl = pageData.profile_id ? `/vendor/${pageData.profile_id}` : null;

  const statsCards = [
    {
      key: 'total',
      title: 'Total areas',
      value: stats.total ?? 0,
      icon: Layers,
      variant: 'default',
    },
    {
      key: 'active',
      title: 'Active on profile',
      value: stats.active ?? 0,
      icon: CheckCircle2,
      variant: 'success',
    },
    {
      key: 'inactive',
      title: 'Inactive',
      value: stats.inactive ?? 0,
      icon: CircleOff,
      variant: stats.inactive > 0 ? 'warning' : 'default',
    },
    {
      key: 'remaining',
      title: 'Slots remaining',
      value: stats.remaining ?? maxLocations,
      icon: MapPinned,
      variant: canAddMore ? 'info' : 'warning',
    },
  ];

  if (isChecking || featureLoading || (hasFeature && locationsLoading) || hasActiveSubscription !== true) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading locations…</p>
        </div>
      </div>
    );
  }

  if (!hasFeature) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-12 flex items-center justify-center px-4">
        <div className="max-w-md w-full rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-50 mb-2">Multi-Location</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            This feature is not included in your current plan. Upgrade to manage multiple service locations and reach clients across different areas.
          </p>
          <Link
            to="/vendor/subscription"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white !rounded-xl font-medium hover:bg-primary-700 transition-colors"
          >
            View Plans
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="max-w-6xl mx-auto space-y-5">
        <VendorPageHeader
          title="Multi-Location"
          subtitle="Show clients everywhere you operate — on your public vendor profile."
          actions={
            <>
              {publicProfileUrl && (
                <Link to={publicProfileUrl} target="_blank" rel="noopener noreferrer">
                  <Button type="button" variant="outline" size="sm" icon={ExternalLink} className="!rounded-xl">
                    View public page
                  </Button>
                </Link>
              )}
              <Button
                type="button"
                size="sm"
                icon={Plus}
                onClick={openCreate}
                disabled={!canAddMore}
                className="!rounded-xl"
              >
                Add location
              </Button>
            </>
          }
        />

        {stats.active > 0 && (
          <p className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/50 rounded-full px-3 py-1 -mt-2">
            <CheckCircle2 className="w-4 h-4" />
            {stats.active} active service {stats.active === 1 ? 'area' : 'areas'} live on your profile
          </p>
        )}

        <VendorGuideCallout
          title={VENDOR_PAGE_GUIDES.multiLocation.title}
          steps={VENDOR_PAGE_GUIDES.multiLocation.steps}
          icon={ListChecks}
        />

        <StatsGrid stats={statsCards} columns={4} />

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-5">
            <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950/40 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Primary business location</h2>
                    <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800/50">
                      HQ
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    This is your registered business address. It is separate from the extra areas you add below.
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{primary.business_name || 'Your business'}</p>
                  {primaryLabel && <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{primaryLabel}</p>}
                  {primary.address && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{primary.address}</p>}
                  {primary.service_radius > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Default service radius: {primary.service_radius} miles from HQ
                    </p>
                  )}
                  <Link
                    to="/vendor/account?tab=business"
                    className="inline-block text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline mt-2"
                  >
                    Edit in business profile
                  </Link>
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Additional service areas</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {locations.length === 0
                      ? 'Add areas beyond your HQ so nearby clients know you serve them.'
                      : `${locations.length} of ${maxLocations} locations used`}
                  </p>
                </div>
                {!canAddMore && (
                  <span className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 px-2 py-1 rounded-full">
                    Maximum {maxLocations} locations reached
                  </span>
                )}
              </div>

              {locations.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-sm p-10 text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                    <MapPin className="w-7 h-7" />
                  </div>
                  <h3 className="text-base font-medium text-gray-900 dark:text-gray-50 mb-1">No extra locations yet</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
                    Add neighborhoods, cities, or regions you serve beyond your primary address. Only active areas appear on your public profile.
                  </p>
                  <Button type="button" icon={Plus} onClick={openCreate} className="!rounded-xl">
                    Add your first location
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {locations.map((location) => (
                    <LocationCard
                      key={location.id}
                      location={location}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onToggle={handleToggle}
                      isToggling={togglingId === location.id}
                      isDeleting={deletingId === location.id}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <aside className="xl:sticky xl:top-24 h-fit">
            <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950/40">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Client preview</h3>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  How your service areas appear on your public profile
                </p>
              </div>

              <div className="p-4 space-y-4">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-950/40 p-3">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Primary location</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{primary.business_name || 'Your business'}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{primaryLabel || 'Set your address in business profile'}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-50 mb-2">Service areas</p>
                  <VendorServiceAreas areas={activeLocations} preview />
                </div>

                {publicProfileUrl && (
                  <Link
                    to={publicProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline"
                  >
                    Open full public profile
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>

      <LocationFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialLocation={editingLocation}
        isSaving={createMutation.isPending || updateMutation.isPending}
        primaryLocation={primary}
      />
    </div>
  );
};

export default VendorMultiLocation;
